import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

const DonationChart = ({ donationHistory }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!donationHistory || donationHistory.length === 0) return;

    // Sort donations by date
    const sortedDonations = [...donationHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Prepare data for chart
    const labels = sortedDonations.map(donation => {
      const date = new Date(donation.date);
      return date.toLocaleDateString('en-IN', { 
        month: 'short', 
        year: 'numeric',
        day: 'numeric'
      });
    });
    
    const data = sortedDonations.map(donation => donation.units || 1);
    
    // Cumulative data for line chart
    const cumulativeData = [];
    let sum = 0;
    data.forEach(value => {
      sum += value;
      cumulativeData.push(sum);
    });

    // Create chart
    if (chartRef.current) {
      // Destroy previous chart if it exists
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
      
      const ctx = chartRef.current.getContext('2d');
      chartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'Units Donated',
              data: data,
              backgroundColor: 'rgba(220, 38, 38, 0.6)',
              borderColor: 'rgba(220, 38, 38, 1)',
              borderWidth: 1,
              borderRadius: 4,
              barPercentage: 0.6,
              categoryPercentage: 0.7
            },
            {
              label: 'Cumulative Donations',
              data: cumulativeData,
              type: 'line',
              fill: false,
              borderColor: 'rgba(79, 70, 229, 1)',
              tension: 0.1,
              pointBackgroundColor: 'rgba(79, 70, 229, 1)',
              pointRadius: 3,
              yAxisID: 'y1'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              grid: {
                display: false
              }
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Units Donated'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              },
              ticks: {
                precision: 0
              }
            },
            y1: {
              position: 'right',
              beginAtZero: true,
              title: {
                display: true,
                text: 'Total Units'
              },
              grid: {
                display: false
              },
              ticks: {
                precision: 0
              }
            }
          },
          plugins: {
            legend: {
              position: 'top'
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                title: function(tooltipItems) {
                  return tooltipItems[0].label;
                },
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  if (context.parsed.y !== null) {
                    label += context.parsed.y + ' units';
                  }
                  return label;
                }
              }
            }
          }
        }
      });
    }

    // Cleanup
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [donationHistory]);

  if (!donationHistory || donationHistory.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No donation history available</p>
      </div>
    );
  }

  return <canvas ref={chartRef} />;
};

export default DonationChart;
